import { t } from 'i18next';
import { inject, injectable, interfaces } from 'inversify';
import { action, autorun, makeObservable } from 'mobx';
import { Bounce, toast } from 'react-toastify';
import { HubNotification, IBaseNotification, ILocalizable, ToastNotification } from '../models/notification.model';
import { INotificationsStore } from '../stores/notifications.store';
import { centerTypeSelector, toastTypeSelector } from './notification.helpers';
import { INotificationHubService } from './notificationHub.service';
import { NotificationTemplate } from './NotificationTemplate';

export interface INotificationsService {
  notificationStore: INotificationsStore;
  remove: (notification: IBaseNotification) => Promise<void>;
  clearHub: () => Promise<void>;
  markAsRead: () => Promise<void>;
  push: (notification: IBaseNotification) => Promise<void>;
  initializeHubAsync: () => Promise<void>;
}

export namespace INotificationsService {
  export const $: interfaces.ServiceIdentifier<INotificationsService> = Symbol('NOTIFICATION_SERVICE');
}

@injectable()
export class NotificationsService implements INotificationsService {
  constructor(
    @inject(INotificationsStore.$) public readonly notificationStore: INotificationsStore,
    @inject(INotificationHubService.$) private readonly hubService: INotificationHubService
  ) {
    makeObservable(this);
    autorun(() => {
      const nonHubNotifications = this.notificationStore.notifications.filter(toastTypeSelector);

      if (nonHubNotifications.length === 0) {
        return;
      }
      const notification = nonHubNotifications.shift();
      if (!notification) {
        return;
      }
      this.notificationStore.notifications.replace(this.notificationStore.notifications.filter((x) => x.id !== notification.id));
      this.handleNotificationShow(notification);
    });
  }

  initializeHubAsync = async (): Promise<void> => {
    await this.hubService.initializeAsync();
  };

  @action
  push = async (notification: IBaseNotification): Promise<void> => {
    if (notification instanceof HubNotification) {
      await this.hubService.pushNotificationAsync(notification);
    } else {
      this.notificationStore.notifications.push(notification);
    }
  };

  clearHub = async (): Promise<void> => {
    const hubNotifications = this.notificationStore.notifications.filter(centerTypeSelector);
    await this.hubService.removeNotificationsAsync(hubNotifications.map((n) => n.id));
  };

  remove = async (notification: IBaseNotification): Promise<void> => {
    await this.hubService.removeNotificationsAsync([notification.id]);
  };

  markAsRead = async (): Promise<void> => {
    const readNotifications = this.notificationStore.notifications.filter((n) => !n.wasRead).map((n) => n.id);
    if (readNotifications.length > 0) {
      await this.hubService.markAsReadNotificationsAsync(readNotifications);
    }
  };

  handleNotificationShow = (notification: IBaseNotification): void => {
    if (notification instanceof ToastNotification) {
      toast(
        <NotificationTemplate
          title={this.getLocalizedContent(notification.title)}
          body={this.getLocalizedContent(notification.body)}
        />,
        {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          closeButton: true,
          progress: undefined,
          theme: 'light',
          transition: Bounce,
          type: notification.level,
        }
      );
    }
  };

  getLocalizedContent = (content?: string | ILocalizable): string => {
    if (typeof content === 'string') {
      return t(content);
    }

    if (content?.template) {
      const data = (content.data ?? {}) as Record<string, string>;
      return t(content.template, data);
    }

    return '';
  };
}
